// apps/web/src/pages/jobs/JobDetailsPage.tsx

import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { trpc } from '@/trpc/client';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { DollarSign, MapPin, Package, Calendar, Loader2, ShieldCheck, Truck, Wallet, Banknote, CheckCircle, XCircle } from 'lucide-react';

const JobDetailsPageSkeleton = () => (
  <div className="container mx-auto py-12 px-4">
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-4 mt-2 w-1/2" />
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <Skeleton className="h-40 w-full" />
        </div>
        <Separator />
        <div className="flex justify-end items-center">
          <Skeleton className="h-12 w-48" />
        </div>
      </CardContent>
    </Card>
  </div>
);

const SecureJobModal = ({ job, vehicles, userRole }: { job: any; vehicles: any[], userRole: string | undefined }) => {
  // We need to trust the prop job.remainingLoads if passed, or rely on invalidation if it changes.
  // Best practice: Query it fresh inside modal or pass it down.
  // Since we modified getById to return remainingLoads, we can assert it exists or fallback.
  const remainingLoads = (job as any).remainingLoads ?? job.loadCount;

  const [isOpen, setIsOpen] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>('');
  const [loadCountToBook, setLoadCountToBook] = useState<number>(remainingLoads);

  // New State for Payments
  const [paymentMethod, setPaymentMethod] = useState<'WALLET' | 'MOBILE_MONEY' | 'PAYCHANGU'>('WALLET');
  const [mobileNumber, setMobileNumber] = useState('');
  const [operatorId, setOperatorId] = useState('airtel_money');
  const [isPolling, setIsPolling] = useState(false);
  const [currentTxRef, setCurrentTxRef] = useState<string | null>(null);

  // Update local state if opening modal and remaining changes (optional, but good for UX)
  useEffect(() => {
    if (isOpen) setLoadCountToBook(Math.min(remainingLoads, job.loadCount));
  }, [isOpen, remainingLoads, job.loadCount]);

  const utils = trpc.useUtils();
  const isFleetManager = userRole === 'FLEET_MANAGER';

  const { data: wallet, isLoading: isLoadingWallet } = trpc.wallet.getBalance.useQuery(undefined, {
    staleTime: 30000,
    enabled: isOpen,
  });

  const balance = wallet?.balance || 0;
  const bookingFee = 50000 * loadCountToBook;
  const hasSufficientBalance = balance >= bookingFee;

  // Polling Logic
  trpc.booking.verifyBookingPayment.useQuery(
    { txRef: currentTxRef! },
    {
      enabled: isPolling && !!currentTxRef,
      refetchInterval: 3000,
      onSuccess: async (data) => {
        if (data.status === 'success') {
          setIsPolling(false);
          setCurrentTxRef(null);
          // Auto-close In-App Browser
          await Browser.close();
          toast.success("Payment Verified! Job Secured.", { description: "You can now view contact details." });
          setIsOpen(false);
          utils.job.getById.invalidate({ id: job.id });
          utils.wallet.getBalance.invalidate();
        }
      }
    }
  );

  const secureJobMutation = trpc.booking.secureJob.useMutation({
    onSuccess: (data) => {
      if ('checkoutUrl' in data && data.checkoutUrl) {
        // Standard: Open Browser AND Start Polling
        toast.info('Opening secure payment page...');
        // Use system browser to ensure deep links work correctly and don't get trapped in WebView
        Browser.open({ url: data.checkoutUrl as string, windowName: '_system' });

        // Start waiting for success
        if (data.txRef) {
          setCurrentTxRef(data.txRef);
          setIsPolling(true);
        }
      } else if (data.status === 'initiated' && data.txRef) {
        // Mobile Money USSD
        toast.success('Payment Initiated', { description: 'Check your phone for the PIN prompt.' });
        setCurrentTxRef(data.txRef);
        setIsPolling(true);
      } else {
        // Wallet Success
        toast.success('Job secured successfully!', { description: 'Payment deducted from wallet.' });
        setIsOpen(false);
        utils.job.getById.invalidate({ id: job.id });
        utils.wallet.getBalance.invalidate();
      }
    },
    onError: (error) => {
      toast.error('Failed to secure job', { description: (error as any).message });
    },
  });

  const handleSecureJob = () => {
    if (!isFleetManager && !selectedVehicleId) {
      toast.error('Please select a vehicle');
      return;
    }

    if (paymentMethod === 'MOBILE_MONEY' && (!mobileNumber || !operatorId)) {
      toast.error('Phone number required for mobile money.');
      return;
    }

    // Map local payment method to API enum (API expects WALLET or PAYCHANGU)
    // If MOBILE_MONEY, we send PAYCHANGU + extra params
    const apiPaymentMethod = paymentMethod === 'WALLET' ? 'WALLET' : 'PAYCHANGU';
    const platform = Capacitor.getPlatform() === 'web' ? 'web' : 'mobile';

    secureJobMutation.mutate({
      jobId: job.id,
      vehicleId: selectedVehicleId || undefined,
      bookedLoadCount: loadCountToBook,
      paymentMethod: apiPaymentMethod,
      mobileNumber: paymentMethod === 'MOBILE_MONEY' ? mobileNumber : undefined,
      operatorId: paymentMethod === 'MOBILE_MONEY' ? operatorId : undefined,
      platform
    });
  };

  const formatCurrency = (amount: number) => `MWK ${amount.toLocaleString()}`;
  const isLoading = secureJobMutation.isLoading;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button size="lg" className="w-full sm:w-auto gap-2 text-base h-12">
          <ShieldCheck className="w-5 h-5" />
          Secure This Job
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[95vw] max-w-[500px] max-h-[90vh] overflow-y-auto p-4 sm:p-6 gap-0">
        <DialogHeader className="mb-4 text-left">
          <DialogTitle className="text-xl sm:text-2xl">Secure Job Booking</DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            {isFleetManager
              ? "Book loads for your fleet."
              : "Select your vehicle and payment method to secure this job."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 sm:space-y-6">
          <div className="bg-muted/50 p-3 sm:p-5 rounded-lg space-y-3 border border-border/50">
            <div className="flex justify-between items-start text-sm gap-2">
              <span className="text-muted-foreground whitespace-nowrap">Job Title:</span>
              <span className="font-medium text-right line-clamp-1">{job.title}</span>
            </div>

            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Total Loads:</span>
              <span className="font-medium text-base">{job.loadCount}</span>
            </div>

            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Available:</span>
              <span className="font-bold text-green-600 dark:text-green-400">{remainingLoads} Loads</span>
            </div>

            <Separator className="bg-border/60" />

            <div className="pt-2 space-y-3">
              <Label className="text-sm font-semibold">Loads to Secure</Label>
              <div className="flex items-center gap-3 sm:gap-6 justify-center bg-background p-2 rounded-md border border-input">
                <Button
                  size="icon" variant="outline"
                  className="h-10 w-10 sm:h-12 sm:w-12 shrink-0"
                  onClick={() => setLoadCountToBook(Math.max(1, loadCountToBook - 1))}
                  disabled={loadCountToBook <= 1}
                >
                  -
                </Button>
                <div className="flex-1 text-center">
                  <Input
                    type="number"
                    min={1}
                    max={remainingLoads}
                    value={loadCountToBook}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (!isNaN(val)) {
                        setLoadCountToBook(Math.max(1, Math.min(remainingLoads, val)));
                      } else if (e.target.value === '') {
                        // Allow empty string while typing
                        setLoadCountToBook(0);
                      }
                    }}
                    onBlur={() => {
                      if (loadCountToBook < 1) setLoadCountToBook(1);
                    }}
                    className="w-full text-center font-mono text-2xl sm:text-3xl font-bold bg-transparent border-none shadow-none focus-visible:ring-0 h-8 sm:h-10 p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Loads</p>
                </div>
                <Button
                  size="icon" variant="outline"
                  className="h-10 w-10 sm:h-12 sm:w-12 shrink-0"
                  onClick={() => setLoadCountToBook(Math.min(remainingLoads, loadCountToBook + 1))}
                  disabled={loadCountToBook >= remainingLoads}
                >
                  +
                </Button>
              </div>
              <p className="text-center text-xs text-muted-foreground">
                You are securing <b>{loadCountToBook}</b> out of <b>{remainingLoads}</b> available.
              </p>
            </div>

            <Separator className="bg-border/60 box-content my-2" />

            <div className="flex flex-col sm:flex-row justify-between items-end sm:items-center gap-1 sm:gap-4 bg-primary/5 p-3 rounded-md">
              <span className="text-sm font-semibold text-foreground/80">Booking Fee:</span>
              <div className="text-right">
                <span className="text-xl sm:text-2xl font-bold text-primary block leading-none">
                  {formatCurrency(bookingFee)}
                </span>
                <span className="text-[10px] text-muted-foreground mt-1 block">
                  Non-refundable deposit
                </span>
              </div>
            </div>
          </div>

          {!isFleetManager && (
            <div className="space-y-2">
              <Label htmlFor="vehicle">Select Your Vehicle *</Label>
              <Select value={selectedVehicleId} onValueChange={setSelectedVehicleId}>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Choose a verified vehicle..." />
                </SelectTrigger>
                <SelectContent>
                  {vehicles?.map((v) => (
                    <SelectItem key={v.id} value={v.id} disabled={!v.verified}>
                      <span className="flex flex-col sm:flex-row sm:gap-2 items-start sm:items-center">
                        <span className="font-medium">{v.make} {v.model}</span>
                        <span className="text-muted-foreground text-xs">({v.licensePlate || 'No Plate'})</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {isFleetManager && (
            <div className="text-sm text-blue-700 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-200 p-3 rounded-md flex gap-3 items-start border border-blue-100 dark:border-blue-900/50">
              <CheckCircle className="w-5 h-5 mt-0.5 shrink-0" />
              <p className="text-xs sm:text-sm leading-relaxed">
                Vehicles and drivers will be assigned from your <b>Dispatch Board</b> after payment.
              </p>
            </div>
          )}

          <Separator />

          {/* Payment Method Toggle */}
          <div className="flex gap-2 p-1 bg-muted rounded-md border border-border/50">
            <Button
              type="button"
              variant={paymentMethod === 'PAYCHANGU' ? 'default' : 'ghost'}
              className="flex-1 text-xs sm:text-sm h-8"
              onClick={() => setPaymentMethod('PAYCHANGU')}
            >
              Pay Now (Card / Mobile)
            </Button>
            <Button
              type="button"
              variant={paymentMethod === 'WALLET' ? 'default' : 'ghost'}
              className="flex-1 text-xs sm:text-sm h-8"
              onClick={() => setPaymentMethod('WALLET')}
            >
              Wallet Balance
            </Button>
          </div>

          {/* PayChangu Info */}
          {paymentMethod === 'PAYCHANGU' && (
            <div className="bg-blue-50 dark:bg-blue-900/10 p-3 rounded-md border border-blue-100 dark:border-blue-900/50">
              <p className="text-sm text-blue-700 dark:text-blue-200 text-center">
                You will be redirected to our secure payment partner to complete your payment via <b>Airtel Money, Mpamba, or Card</b>.
              </p>
            </div>
          )}

          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between bg-muted/30 p-2 rounded">
              <span className="text-sm font-medium flex items-center gap-2">
                <Wallet className="h-4 w-4 text-muted-foreground" />
                Your Wallet:
              </span>
              <span className={`font-bold text-base ${balance >= bookingFee ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                {isLoadingWallet ? <Loader2 className="h-4 w-4 animate-spin" /> : formatCurrency(balance)}
              </span>
            </div>

            {/* Warnings */}
            {paymentMethod === 'WALLET' && !hasSufficientBalance && (
              <Alert variant="destructive" className="py-2">
                <AlertDescription className="flex items-center justify-between text-xs sm:text-sm">
                  <span>Need <b>{formatCurrency(bookingFee - balance)}</b> more.</span>
                  <Link to="/dashboard/wallet" className="underline font-bold whitespace-nowrap ml-2">Top Up</Link>
                </AlertDescription>
              </Alert>
            )}

            <Button
              type="button"
              variant="default"
              size="lg"
              className="w-full h-12 text-base font-semibold shadow-md relative overflow-hidden"
              disabled={
                isLoading ||
                (!isFleetManager && !selectedVehicleId) ||
                (paymentMethod === 'WALLET' && !hasSufficientBalance) ||
                (paymentMethod === 'MOBILE_MONEY' && (!mobileNumber || !operatorId))
              }
              onClick={handleSecureJob}
            >
              {isLoading ? (
                <div className="absolute inset-0 bg-primary/50 flex items-center justify-center backdrop-blur-[1px]">
                  <Loader2 className="h-5 w-5 animate-spin text-white" />
                </div>
              ) : (
                paymentMethod === 'WALLET' ? <Wallet className="mr-2 h-5 w-5" /> :
                  paymentMethod === 'MOBILE_MONEY' ? <CheckCircle className="mr-2 h-5 w-5" /> :
                    <Banknote className="mr-2 h-5 w-5" />
              )}
              {isPolling ? 'Confirming Payment...' :
                paymentMethod === 'MOBILE_MONEY' ? `Pay ${formatCurrency(bookingFee)} (USSD)` :
                  paymentMethod === 'PAYCHANGU' ? `Pay ${formatCurrency(bookingFee)} (Card)` :
                    `Pay Now • ${formatCurrency(bookingFee)}`}
            </Button>

            {isPolling && (
              <p className="text-xs text-center text-muted-foreground animate-pulse">
                Check your phone for the PIN prompt.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="text-xs text-muted-foreground">
          Need to top up?{' '}
          <Link to="/dashboard/wallet" className="text-primary hover:underline">
            Visit your wallet
          </Link>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const JobDetailsPage = () => {
  const utils = trpc.useUtils();
  const { jobId } = useParams<{ jobId: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const isTransporter = user?.role === 'TRANSPORTER';
  const isFleetManager = user?.role === 'FLEET_MANAGER';
  const canBook = isTransporter || isFleetManager;

  const bookingSecured = searchParams.get('booking_secured') === 'true';

  const { data: job, isLoading, isError } = trpc.job.getById.useQuery({ id: jobId! }, { enabled: !!jobId });

  // Fetch all vehicles, UI will filter disabled/unverified
  const { data: vehicles } = trpc.vehicle.getMyVehicles.useQuery(undefined, {
    enabled: canBook,
  });

  if (isLoading) return <JobDetailsPageSkeleton />;

  if (isError || !job) {
    return (
      <div className="container py-24 text-center">
        <p className="text-destructive">Job not found or an error occurred.</p>
      </div>
    );
  }

  // If job is ACTIVE, it is not yet secured by a transporter
  const hasSecuredJob = job.status !== 'ACTIVE';

  // Can secure if Active AND checking conditions
  const hasVerifiedVehicles = vehicles?.some(v => v.verified);

  const canSecureJob =
    canBook &&
    job.status === 'ACTIVE' &&
    (isFleetManager || (hasVerifiedVehicles));

  const cannotSecureReason =
    job.status !== 'ACTIVE'
      ? `This job is ${job.status.toLowerCase()} and cannot be secured.`
      : canBook && !isFleetManager && (!hasVerifiedVehicles)
        ? 'You need a verified vehicle to secure jobs.'
        : '';


  return (
    <div className="container mx-auto py-4 px-2 sm:py-12 sm:px-4 animate-fade-in relative">
      {hasSecuredJob && isTransporter && (
        <Alert className="max-w-4xl mx-auto mb-6 border-green-500 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertTitle>Job Secured!</AlertTitle>
          <AlertDescription>Job is secured and contact details are visible.</AlertDescription>
        </Alert>
      )}

      <Card className="max-w-4xl mx-auto mb-20 md:mb-0">
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-semibold text-primary">K&C Logistics Package</p>
              <CardTitle className="text-3xl font-bold mt-1">{job.title}</CardTitle>
            </div>
            <Badge variant={job.status === 'ACTIVE' ? 'default' : 'secondary'} className="text-lg">
              {job.status}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <MapPin className="text-primary w-6 h-6 shrink-0 mt-1" />
              <div>
                <p className="font-semibold">Pickup Location</p>
                <p className="text-muted-foreground">
                  {job.pickupAddress}, {job.pickupDistrict}
                </p>
                {hasSecuredJob && (
                  <div className="mt-2 text-sm">
                    <p className="text-green-600 font-medium">Contact: {job.pickupContactName}</p>
                    <p className="text-green-600">{job.pickupContactPhone}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <MapPin className="text-accent w-6 h-6 shrink-0 mt-1" />
              <div>
                <p className="font-semibold">Delivery Location</p>
                <p className="text-muted-foreground">
                  {job.deliveryAddress}, {job.deliveryDistrict}
                </p>
                {hasSecuredJob && (
                  <div className="mt-2 text-sm">
                    <p className="text-green-600 font-medium">Contact: {job.deliveryContactName}</p>
                    <p className="text-green-600">{job.deliveryContactPhone}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="font-semibold text-muted-foreground">Client Offer</p>
              <p className="text-2xl font-bold flex items-center justify-center gap-1">
                <DollarSign className="w-5 h-5" />
                {parseFloat(job.priceAmount as any).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="font-semibold text-muted-foreground">Loads</p>
              <p className="text-2xl font-bold flex items-center justify-center gap-2">
                <Truck className="w-5 h-5" />
                {job.loadCount}
              </p>
            </div>
            <div>
              <p className="font-semibold text-muted-foreground">Cargo Type</p>
              <p className="text-2xl font-bold flex items-center justify-center gap-2">
                <Package className="w-5 h-5" />
                {job.cargoType}
              </p>
            </div>
            <div>
              <p className="font-semibold text-muted-foreground">Posted</p>
              <p className="text-lg font-bold">
                {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
              </p>
            </div>
          </div>

          {job.description && (
            <>
              <Separator />
              <div>
                <h3 className="font-semibold text-xl mb-2">Job Description</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">{job.description}</p>
              </div>
            </>
          )}

          <Separator />

          {/* Desktop Action Buttons */}
          <div className="hidden md:flex justify-end items-center">
            {canSecureJob && <SecureJobModal job={job} vehicles={vehicles || []} userRole={user?.role} />}

            {!canSecureJob && isTransporter && cannotSecureReason && (
              <Alert variant="default">
                <AlertDescription>{cannotSecureReason}</AlertDescription>
              </Alert>
            )}

            {job.status !== 'ACTIVE' && job.status !== 'CANCELLED' && job.status !== 'COMPLETED' && (
              <Button asChild variant="outline" className="gap-2 border-primary text-primary hover:bg-primary/10">
                <Link to={`/dashboard/jobs/${job.id}/track`}>
                  <MapPin className="w-4 h-4" />
                  Live Tracking
                </Link>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Mobile Sticky Footer */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border md:hidden z-40 pb-safe">
        {canSecureJob ? (
          <div className="w-full">
            <SecureJobModal job={job} vehicles={vehicles || []} userRole={user?.role} />
          </div>
        ) : job.status !== 'ACTIVE' && job.status !== 'CANCELLED' && job.status !== 'COMPLETED' ? (
          <Button asChild variant="outline" className="w-full gap-2 border-primary text-primary hover:bg-primary/10 h-12 text-lg">
            <Link to={`/dashboard/jobs/${job.id}/track`}>
              <MapPin className="w-5 h-5" />
              Live Tracking
            </Link>
          </Button>
        ) : (
          !canSecureJob && isTransporter && cannotSecureReason && (
            <div className="text-center text-xs text-muted-foreground">
              {cannotSecureReason}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default JobDetailsPage;
